import { ActionTree } from 'vuex';
import { DiscussionState } from './state';
import RootState from '@/store/root_state';
import { discussion as discussionSchema } from '@/store/forumAPI/forum_api_normalizr_schema';
import { normalize } from 'normalizr';
import { ErrorHandler } from '@/helpers/errorHandler';
import i18n from '@/i18n';
import { FileModel } from '@/models/FileModel';
import { DiscussionService } from '@/services/forum/discussion.service';

export interface RawDiscussion {
  name: string;
  text: string;
  userId: number;
  channelId: number;
  id: number;
  attachments: FileModel[];
}

const actions: ActionTree<DiscussionState, RootState> = {
  async CREATE_DISCUSSION({ commit }, payload: RawDiscussion) {
    try {
      const { name, text, userId, channelId, attachments } = payload;
      const createdDiscussion = await DiscussionService.createDiscussion({
        name,
        text,
        channelId,
        userId,
        attachments,
      });

      const normalizedResponse = normalize(createdDiscussion.data, {
        upsertDiscussion: discussionSchema,
      });

      commit('SET_FORUM_API_DISCUSSIONS', normalizedResponse.entities);
      commit('SET_FORUM_API_PAGINATED_DISCUSSIONS', normalizedResponse.entities);

      return normalizedResponse.result.upsertDiscussion;
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('errorCreatingDiscussion').toString(),
      });

      return undefined;
    }
  },

  async DELETE_FORUM_DISCUSSION({ commit }, discussion: RawDiscussion) {
    try {
      await DiscussionService.deleteDiscussion(discussion.id);

      commit('DELETE_FORUM_DISCUSSION', {
        discussionId: discussion.id,
      });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToDeleteDiscussion').toString(),
      });
    }
  },

  async UPDATE_FORUM_DISCUSSION(
    { commit },
    payload: { text: string; discussionId: number; attachments: FileModel[] },
  ) {
    try {
      const { text, attachments, discussionId } = payload;

      await DiscussionService.updateDiscussion({
        text,
        attachments,
        discussionId,
      });

      commit('UPDATE_FORUM_DISCUSSION', {
        text,
        attachments,
        discussionId,
      });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToEditDiscussion').toString(),
      });
    }
  },
};

export default actions;
